import { oktaAuth } from '../config/auth.config';

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  backoffMultiplier: number;
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  backoffMultiplier: 2,
};

export class TokenRefreshService {
  private static instance: TokenRefreshService;
  private refreshPromise: Promise<void> | null = null;
  private retryConfig: RetryConfig;

  private constructor(retryConfig: RetryConfig = defaultRetryConfig) {
    this.retryConfig = retryConfig;
  }

  public static getInstance(retryConfig?: RetryConfig): TokenRefreshService {
    if (!TokenRefreshService.instance) {
      TokenRefreshService.instance = new TokenRefreshService(retryConfig);
    }
    return TokenRefreshService.instance;
  }

  /**
   * Refresh tokens with automatic retry logic
   */
  public async refreshTokens(): Promise<void> {
    // If a refresh is already in progress, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefreshWithRetry();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Get access token with automatic refresh if expired
   */
  public async getAccessToken(): Promise<string | null> {
    try {
      const tokenManager = oktaAuth.tokenManager;
      let accessToken = await tokenManager.get('accessToken');

      // Check if token exists and is not expired
      if (accessToken && !tokenManager.hasExpired(accessToken)) {
        return (accessToken as any).value || (accessToken as any).accessToken;
      }

      // Token is expired or doesn't exist, try to refresh
      await this.refreshTokens();

      // Get the refreshed token
      accessToken = await tokenManager.get('accessToken');
      return (accessToken as any)?.value || (accessToken as any)?.accessToken || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Check if the user's session is still valid
   */
  public async isSessionValid(): Promise<boolean> {
    try {
      const authState = await oktaAuth.authStateManager.getAuthState();
      return authState?.isAuthenticated ?? false;
    } catch (error) {
      console.error('Failed to check session validity:', error);
      return false;
    }
  }

  /**
   * Setup automatic token refresh
   */
  public setupAutoRefresh(): void {
    oktaAuth.tokenManager.on('expired', async (key, expiredToken) => {
      console.log(`Token ${key} expired:`, expiredToken);
      
      try {
        await this.refreshTokens();
        console.log(`Successfully refreshed ${key} token`);
      } catch (error) {
        console.error(`Failed to refresh ${key} token:`, error);
        // If refresh fails, the user will be logged out by the AuthProvider
      }
    });

    oktaAuth.tokenManager.on('error', (error) => {
      console.error('Token manager error:', error);
    });
  }

  /**
   * Perform token refresh with retry logic
   */
  private async performRefreshWithRetry(): Promise<void> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          const delay = this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt - 1);
          console.log(`Retrying token refresh (attempt ${attempt}/${this.retryConfig.maxRetries}) in ${delay}ms...`);
          await this.sleep(delay);
        }

        await this.performTokenRefresh();
        console.log('Token refresh successful');
        return;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown refresh error');
        console.error(`Token refresh attempt ${attempt + 1} failed:`, error);

        // Don't retry on certain errors
        if (this.isNonRetryableError(error)) {
          throw error;
        }
      }
    }

    // All retries failed
    throw new Error(`Token refresh failed after ${this.retryConfig.maxRetries} retries. Last error: ${lastError?.message}`);
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<void> {
    const tokenManager = oktaAuth.tokenManager;

    // Try to renew both access and ID tokens
    await Promise.all([
      tokenManager.renew('accessToken'),
      tokenManager.renew('idToken'),
    ]);
  }

  /**
   * Check if an error should not be retried
   */
  private isNonRetryableError(error: any): boolean {
    // Don't retry on authentication errors
    if (error?.name === 'AuthApiError' && error?.errorCode === 'invalid_grant') {
      return true;
    }

    // Don't retry on network errors that indicate permanent issues
    if (error?.message?.includes('Network request failed')) {
      return false; // Actually, network errors should be retried
    }

    return false;
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear all tokens and reset state
   */
  public async clearTokens(): Promise<void> {
    try {
      await oktaAuth.tokenManager.clear();
      this.refreshPromise = null;
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }
}

// Export singleton instance
export const tokenRefreshService = TokenRefreshService.getInstance();

// HTTP interceptor function for adding auth headers
export const addAuthHeader = async (config: RequestInit = {}): Promise<RequestInit> => {
  const token = await tokenRefreshService.getAccessToken();
  
  if (token) {
    const headers = new Headers(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    
    return {
      ...config,
      headers,
    };
  }

  return config;
};

// Fetch wrapper with automatic auth header injection
export const authenticatedFetch = async (url: string, config: RequestInit = {}): Promise<Response> => {
  const authConfig = await addAuthHeader(config);
  return fetch(url, authConfig);
};